import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { NewsContentType } from '@/types/newsService';
import { cn } from '@/lib/cn';

interface Props {
  activeContentType: NewsContentType | null;
  onContentTypeChange: (type: NewsContentType | null) => void;
  availableContentTypes: NewsContentType[];
}

interface TabItem {
  key: string;
  label: string;
  value: NewsContentType | null;
}

const CONTENT_TYPE_LABELS: Record<string, string> = {
  news: 'News',
  analysis: 'Analysis',
  opinion: 'Opinion',
  guide: 'Guide',
  recap: 'Recap',
  interview: 'Interview',
};

/**
 * Sub-filter row on the news feed: "Latest" (= all content types) plus a tab
 * per content type that's actually present in the current feed result.
 * Ported from mobile-app/src/components/news/FilterTabs.tsx.
 */
export const FilterTabs = memo(function FilterTabs({
  activeContentType,
  onContentTypeChange,
  availableContentTypes,
}: Props) {
  const { t } = useTranslation('news');
  const tabs = useMemo<TabItem[]>(
    () => [
      { key: 'latest', label: t('filter.all', { defaultValue: 'Latest' }), value: null },
      ...availableContentTypes.map((ct) => ({
        key: ct,
        label: t(`contentType.${ct}`, { defaultValue: CONTENT_TYPE_LABELS[ct] ?? ct }),
        value: ct,
      })),
    ],
    [availableContentTypes, t],
  );

  return (
    <div className="no-scrollbar overflow-x-auto">
      <div className="flex gap-1.5 px-4 py-1.5">
        {tabs.map((tab) => {
          const isActive = activeContentType === tab.value;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onContentTypeChange(tab.value)}
              className={cn(
                'whitespace-nowrap rounded-[14px] px-3.5 py-1.5 text-[13px] font-semibold transition-opacity active:opacity-80',
                isActive ? 'bg-brand/15 text-brand' : 'text-fg-muted',
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
});
